import React, { useEffect, useRef, useState } from "react";
import { Button, Tooltip } from "antd";
import { Deal } from "../../../../interfaces/deal.interface";

// Tracks whether an element's text is actually ellipsis-clipped (scrollWidth >
// clientWidth), re-checking via ResizeObserver since the card renders at a
// fixed width in the homepage carousel but a variable width in the listing
// page's responsive grid.
function useTruncated<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setTruncated(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, truncated] as const;
}

// The ticket-shaped deal card, shared by the homepage's Exclusive Deals
// carousel and the deals listing page (see ../ExclusiveDealsListing). Title
// and subtitle are single-line with ellipsis overflow (see .v2-deal-card__body
// in v2.scss); a tooltip reveals the full text only when it's actually cut off.
const DealCard: React.FC<{ deal: Deal }> = ({ deal }) => {
  const [titleRef, titleTruncated] = useTruncated<HTMLHeadingElement>();
  const [subtitleRef, subtitleTruncated] = useTruncated<HTMLParagraphElement>();

  return (
    <div className="v2-deal-card">
      <span className="v2-deal-card__deco" aria-hidden="true" />
      <div className="v2-deal-card__headline">{deal.headline}</div>
      <span className="v2-deal-card__perf" aria-hidden="true" />
      <div className="v2-deal-card__body">
        <Tooltip title={titleTruncated ? deal.title : undefined}>
          <h3 ref={titleRef}>{deal.title}</h3>
        </Tooltip>
        <Tooltip title={subtitleTruncated ? deal.subtitle : undefined}>
          <p ref={subtitleRef}>{deal.subtitle}</p>
        </Tooltip>
        <Button className="v2-deal-card__cta" size="small" href={deal.redirectUrl || undefined} target="_blank" rel="noopener noreferrer">{deal.cta}</Button>
      </div>
    </div>
  );
};

export default DealCard;
